import {
  DataGridPro,
  GridCellParams,
  GridColDef,
  GridFilterItem,
  GridLogicOperator,
  GridRowSelectionModel,
  getGridDateOperators,
  getGridSingleSelectOperators,
  getGridStringOperators,
  gridClasses,
  useGridApiRef,
} from '@mui/x-data-grid-pro';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch } from 'react-redux';
import {
  ActionApprovalLogType,
  ActionStatus,
  actionSubStatusFilterLabels,
  ActionType,
  currencyFormatter,
  IContact,
  IOrganisationResponseItem,
  langauge,
  PRIMARY_CONTACT_NAME_SORT_COLUMN,
  SavedFilterGridState,
  SavedFilterType,
  SendgridMessageEventType,
  toDecimal,
} from 'shared';
import { DateTime } from 'luxon';
import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import { useLocation } from 'react-router-dom';
import { When } from 'react-if';
import { Tooltip } from '@mui/material';
import {
  BackwardIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationCircleIcon,
  HandRaisedIcon,
  MagnifyingGlassCircleIcon,
  NoSymbolIcon,
  PauseCircleIcon,
} from '@heroicons/react/20/solid';
import { GridToolbarProps } from '@mui/x-data-grid/internals';
import { toast } from 'react-toastify';

import { DatePopover } from '../../../common/Components/DatePopover';
import {
  useActionParams,
  useActions,
  useActionTableModels,
} from '../../hooks/useActions';
import { usePersistedTableModels } from "../../hooks/usePersistedTableModels";
import { WrappedLink } from '../WrappedLink';
import { open } from '../../slices/actionDrawer';
import { useExportAllActionsMutation } from '../../services/api/actionApi/action';
import { Paragraph } from '../../../common/Atoms/Typography/Paragraph';
import { Badge } from '../../../common/Atoms/Badge';
import { usePolicies } from '../../hooks/usePolicies';
import { useGetSelectedOrganisation } from '../../hooks/useGetSelectedOrganisation';
import { useAccountingSystemTerm } from '../../hooks/useAccountingSystemTerm';
import { ITab, Tabs } from '../../../common/Atoms/Tabs';
import { useIsSafeMode } from '../../hooks/useIsSafeMode';
import { Strap } from '../../../common/Atoms/Strap';
import {
  commonConfig,
  filterColumns,
  getColumnForNewFilter,
  onRowSelectionChangeWrapper,
  toSavedFilterExportParams,
} from '../../lib/datagrid';
import { CustomGenericToolbar } from '../Common/Datagrid/CustomGenericToolbar';
import { DatagridCard } from '../Common/Datagrid/DatagridCard';
import { ApiSavedFilterPanel } from '../Common/Datagrid/ApiSavedFilterPanel';
import { SaveFilterButton } from '../Common/Datagrid/SaveFilterButton';
import { useApplySavedFilterFromUrl } from '../../hooks/useApplySavedFilterFromUrl';
import { emailStatusColor, emailStatusLabel } from '../Common/EmailStatusPill';

import { ActionStatusBadge } from './ActionStatusBadge';
import { BulkUpdatesToolbar } from './BulkUpdatesToolbar';

interface ActionsTableProps {
  omitColumns?: string[];
  smallTabs?: boolean;
}

// augment the props for the toolbar slot
// eslint-disable-next-line quotes
declare module "@mui/x-data-grid"{
  interface ToolbarPropsOverrides {
    currentOrg: IOrganisationResponseItem;
    rowSelectionModel: GridRowSelectionModel;
    onRemoveFilter: (filterId: GridFilterItem[`id`]) => void;
  }
}

type ToolbarProps = GridToolbarProps & {
  onRemoveFilter: (filterId: GridFilterItem[`id`]) => void;
  rowSelectionModel: GridRowSelectionModel;
  currentOrg: IOrganisationResponseItem;
};

const defaultColumnVisibilityModel = {
  policyId: false,
  createdInvoiceNumber: false,
  invoiceTotal: false,
  systemActions: false,
  toEmail: false,
  policyNames: false,
  emailStatus: false,
  subStatus: false,
  [PRIMARY_CONTACT_NAME_SORT_COLUMN]: false,
};

export function ActionsTable({ omitColumns = [], smallTabs }: ActionsTableProps) {
  const { search } = useLocation();
  const tabsRef = useRef<HTMLDivElement>(null);
  const urlParams = new URLSearchParams(search);
  const { data: actions, isLoading, isFetching } = useActions();
  const { resetModels, currentTab, setCurrentTab } = useActionTableModels();
  const savedFilterExportParams = toSavedFilterExportParams(useActionParams());
  const dispatch = useDispatch();
  const { policyFilters } = usePolicies();
  const gridApiRef = useGridApiRef();
  const currentOrg = useGetSelectedOrganisation();
  const quoteTerm = useAccountingSystemTerm(`Quote`);
  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>({
    type: `include`,
    ids: new Set(),
  });
  const safeModeEnabled = useIsSafeMode();

  // The controlled state
  const actionTableModels = useActionTableModels();
  const {
    filterModel,
    sortModel,
    paginationModel,

    setFilterModel,
    setSortModel,
    setPaginationModel,
  } = actionTableModels;
  const restoredModels = usePersistedTableModels(`action`, actionTableModels);

  // Saved filters also carry the status tab (gridState.extra.tab), applied
  // before the grid state so the tab change doesn't strip the restored filters
  const onApplySavedFilter = useCallback((gridState: SavedFilterGridState) => {
    const tab = gridState.extra?.tab;

    if (typeof tab === `string` && Object.values(ActionStatus).includes(tab as ActionStatus)) {
      setCurrentTab(tab as ActionStatus);
    }
  }, [setCurrentTab]);

  const savedFilterId = useApplySavedFilterFromUrl(gridApiRef, SavedFilterType.Action, {
    defaultColumnVisibilityModel,
    onApply: onApplySavedFilter,
  });

  // On first mount, reset the filters, unless we're restoring a saved filter
  useEffect(() => {
    if (urlParams.has(`persist`) || savedFilterId || restoredModels) {
      // Do nothing
    }
    else {
      resetModels();
    }
  }, []);

  const onRemoveFilter = (filterId: GridFilterItem[`id`]) => {
    setFilterModel({
      items: filterModel.items.filter(item => item.id !== filterId),
    });
  };

  function onCellClick(params: GridCellParams) {
    const fieldsWithLinks = [
      `contact`,
      `__check__`,
    ];
    if (fieldsWithLinks.includes(params.field)) {
      return;
    }

    dispatch(open(params.row.id));
  }

  const typeOptions = Object.values(ActionType).map(at => ({
    label: langauge.action[at].label,
    value: at,
  }));

  const rows = useMemo(() => {
    const result = actions?.actions.map(action => {
      const scheduledDisplay = (
        <DatePopover
          date={ action.actionOn }
          labelFormatPreset={ DateTime.DATETIME_MED }
          organisationTimezone={ action.organisationTimezone }
        />
      );

      return {
        ...action,
        typeDisplay: langauge.action[action.type].label,
        statusDisplay: (
          <div className={ `flex flex-col items-start gap-1` }>
            <ActionStatusBadge
              action={ action }
              size={ `sm` }
            />
            <When condition={ !!action.latestEmailStatus }>
              { () => (
                <Badge
                  color={ emailStatusColor(action.latestEmailStatus as string) }
                  message={ emailStatusLabel(action.latestEmailStatus as string) }
                  size={ `sm` }
                />
              ) }
            </When>
          </div>
        ),
        scheduledDisplay,
        executedDisplay: action.executedOn ? <DatePopover
          date={ action.executedOn }
          labelFormatPreset={ DateTime.DATETIME_MED }
          organisationTimezone={ action.organisationTimezone }
        /> : `-`,
        invoiceNumber: action.invoice?.xero_number || action.quote?.number || `-`,
        invoiceTotalInCents: action.invoice?.xero_amount_total ?? null,
        invoiceCurrency: action.invoice?.xero_currency,
        quoteNumber: action.quote?.number || `-`,
        createdInvoiceNumber: action.penalty?.xero_invoice_number || `-`,
        toEmail: action.payload?.toAddresses?.join(`, `) || action.payload?.contactPerson?.email || ``,
        policyNames: action.sourcePolicies || ``,
        emailStatus: action.latestEmailStatus || null,
        subStatus: action.subStatus || null,
      };
    });

    return result || [];
  }, [actions?.actions]);

  const columns: GridColDef<(typeof rows)[number]>[] = useMemo(() => {
    const r: GridColDef<(typeof rows)[number]>[] = [
      {
        field: `type`,
        headerName: `Type`,
        width: 180,
        cellClassName: `cursor-pointer`,
        type: `singleSelect`,
        filterOperators: getGridSingleSelectOperators().filter(op => op.value === `is`),
        valueOptions: typeOptions,
        renderCell: ({ row }) => <span className={ `w-full` }>
          <span className={ `flex` }>
            { row.typeDisplay }
            <Paragraph variant={ `link` }
              as={ `span` }>
              <ArrowTopRightOnSquareIcon className={ `h-4 w-4 inline-block ml-1` } />
            </Paragraph>
          </span>
          <When condition={ row.sourcePolicies }>
            { () => (
              <Tooltip title={ `Created by: ${row.sourcePolicies === `SYSTEM POLICY` ? `manual trigger` : row.sourcePolicies }` }>
                <span>
                  <Paragraph
                    variant={ `help` }
                    as={ `span` }
                    className={ `block truncate` }
                  >
                    { row.sourcePolicies }
                  </Paragraph>
                </span>
              </Tooltip>
            ) }
          </When>

          <When condition={ !!row.latestApprovalLog && row.latestApprovalLog.logType === ActionApprovalLogType.APPROVED }>
            { () => (
              <Paragraph
                variant={ `help` }
              >
                { `Approved by ${row.latestApprovalLog.userEmail} ${row.wasForced ? ` and run immediately` : ``}` }
              </Paragraph>
            ) }
          </When>

          <When condition={ !!row.latestApprovalLog && row.latestApprovalLog.logType === ActionApprovalLogType.APPROVAL_REVERTED }>
            { () => (
              <Paragraph
                variant={ `help` }
              >
                { `Reverted by ${row.latestApprovalLog.userEmail}` }
              </Paragraph>
            ) }
          </When>

          <When condition={ !!row.latestApprovalLog && row.latestApprovalLog.logType === ActionApprovalLogType.RUN_NOW }>
            { () => (
              <Paragraph
                variant={ `help` }
              >
                { `Run now by ${row.latestApprovalLog.userEmail || `Unknown`}` }
              </Paragraph>
            ) }
          </When>

          <When condition={ !!row.latestApprovalLog && row.latestApprovalLog.logType === ActionApprovalLogType.CREATED_ADHOC }>
            { () => (
              <Paragraph
                variant={ `help` }
              >
                { `Created by ${row.latestApprovalLog.userEmail || `Unknown`}` }
              </Paragraph>
            ) }
          </When>
        </span>,
      },
      {
        field: `status`,
        width: 100,
        cellClassName: `cursor-pointer`,
        headerName: `Status`,
        filterable: false,
        renderCell: ({ row }) => row.statusDisplay,
      },
      {
        field: `contact`,
        headerName: `Contact`,
        type: `string`,
        width: 180,
        filterOperators: getGridStringOperators().filter(op => op.value === `contains`),
        renderCell: ({ row }) => row.contact?.id ? (
          <span>
            <WrappedLink to={ `/contacts/${row.contact.id}` }>
              <span className={ `text-blue-600 underline whitespace-wrap` }>
                { row.contact.name }
              </span>
            </WrappedLink>
            <When condition={ row.toEmail && row.type !== ActionType.SEND_SMS }>
              <Tooltip title={ row.toEmail }>
                <Paragraph
                  variant={ `help` }
                  as={ `span` }
                  className={ `block` }
                >
                  {
                    row.toEmail.length > 50
                      ? `${row.toEmail.slice(0, 50)}...`
                      : row.toEmail
                  }
                </Paragraph>
              </Tooltip>
            </When>

            <When condition={ row.payload?.smsPayload?.to && row.type === ActionType.SEND_SMS }>
              <Paragraph
                variant={ `help` }
                as={ `span` }
                className={ `block` }
              >
                { row.payload?.smsPayload?.to }
              </Paragraph>
            </When>

          </span>
        ) : row.invoice?.xero_contact_name || row.contact?.name || `-`,
        sortable: false,
        valueFormatter: value => {

          return (value as IContact)?.name || `-`;
        },
      },
      {
        field: PRIMARY_CONTACT_NAME_SORT_COLUMN,
        headerName: `Primary Contact`,
        type: `string`,
        width: 180,
        filterOperators: getGridStringOperators().filter(op => op.value === `contains`),
        renderCell: ({ row }) => row.contact?.id ? (
          <span>
            <WrappedLink to={ `/contacts/${row.contact.id}` }>
              <span className={ `text-blue-600 underline whitespace-wrap` }>
                { row.contact?.primaryContactName }
              </span>
            </WrappedLink>

          </span>
        ) : row.contact?.primaryContactName || `-`,
        sortable: false,
      },
      {
        field: `invoiceNumber`,
        headerName: `Invoice / ${quoteTerm}`,
        cellClassName: `cursor-pointer`,
        filterOperators: getGridStringOperators().filter(op => op.value === `contains`),
        type: `string`,
      },
      {
        field: `actionOn`,
        headerName: `Scheduled`,
        renderCell: ({ row }) => row.scheduledDisplay,
        filterOperators: getGridDateOperators(true).filter(op => {
          return [ `onOrAfter`, `onOrBefore`].includes(op.value);
        }),
        width: 120,
      },
      {
        field: `executedOn`,
        headerName: `Actioned On`,
        width: 120,
        cellClassName: `cursor-pointer`,
        type: `custom`,
        filterOperators: getGridDateOperators(true).filter(op => {
          return [ `onOrAfter`, `onOrBefore`].includes(op.value);
        }),
        renderCell: ({ row }) => row.executedDisplay,
      },
      // Hidden - rendered under the status badge, kept as a column so it stays filterable
      {
        field: `emailStatus`,
        headerName: `Latest Email Status`,
        type: `singleSelect`,
        sortable: false,
        valueOptions: Object.values(SendgridMessageEventType).map(status => ({
          value: status,
          label: emailStatusLabel(status),
        })),
        // Multi-select only, applied server-side
        filterOperators: getGridSingleSelectOperators().filter(op => op.value === `isAnyOf`),
        valueFormatter: value => value ? emailStatusLabel(value as string) : `-`,
      },
      // Hidden - rendered in the status badge, kept as a column so it stays filterable
      {
        field: `subStatus`,
        headerName: `Sub Status`,
        type: `singleSelect`,
        sortable: false,
        valueOptions: Object.entries(actionSubStatusFilterLabels).map(([ value, label ]) => ({ value, label })),
        // Multi-select only, applied server-side
        filterOperators: getGridSingleSelectOperators().filter(op => op.value === `isAnyOf`),
        valueFormatter: value => value ? actionSubStatusFilterLabels[value as keyof typeof actionSubStatusFilterLabels] : `-`,
      },
      {
        field: `policyId`,
        headerName: `Policy`,
        type: `singleSelect`,
        filterOperators: getGridSingleSelectOperators().filter(op => op.value === `is`),
        valueOptions: policyFilters,
      },
      {
        field: `createdInvoiceNumber`,
        headerName: `Created Invoice`,
        type: `string`,
      },
      {
        field: `invoiceTotal`,
        headerName: `Invoice Total`,
        type: `number`,
        valueGetter: (_value, row) => row.invoiceTotalInCents != null
          ? toDecimal(row.invoiceTotalInCents)
          : null,
        valueFormatter: (value, row) => {
          if (value == null) return `-`;

          return row.invoiceCurrency
            ? currencyFormatter(row.invoiceCurrency)(value as number)
            : String(value);
        },
      },
      {
        field: `systemActions`,
        headerName: `Manual Action`,
        type: `boolean`,
        valueGetter: (_value, row) => !!row.fromSystemPolicy,
      },
      {
        field: `toEmail`,
        headerName: `Email Address`,
        type: `string`,
      },
      {
        field: `policyNames`,
        headerName: `Policy Names`,
        type: `string`,
      },
    ];

    // Apply some duplicates
    return r.map(c => ({
      ...c,
      editable: false,
      headerClassName: `text-md text-gray-800 font-bold`,
    }))
      .filter(r => !omitColumns.includes(r.field));
  }, [rows, policyFilters, omitColumns, quoteTerm]);

  const hiddenFields = [`policyId`, `createdInvoiceNumber`, `systemAction`, `toEmail`, `policyNames`, `emailStatus`];

  const getTogglableColumns = (columns: GridColDef[]) => {
    return columns
      .filter(column => !hiddenFields.includes(column.field))
      .filter(column => column.type !== `actions`)
      .map(column => column.field);
  };

  const tabs: ITab[] = useMemo(() => {
    return [
      {
        name: langauge.generic.action.status.pending,
        onClick: () => setCurrentTab(ActionStatus.PENDING),
        icon: ClockIcon,
        href: `#`,
        current: currentTab === ActionStatus.PENDING,
        badge: actions?.statusTotals[ActionStatus.PENDING] ? {
          text: actions.statusTotals[ActionStatus.PENDING].toString(),
          color: ``,
        } : undefined,
      },
      {
        name: langauge.generic.action.status.approval_pending,
        onClick: () => setCurrentTab(ActionStatus.APPROVAL_PENDING),
        icon: HandRaisedIcon,
        href: `#`,
        current: currentTab === ActionStatus.APPROVAL_PENDING,
        badge: actions?.statusTotals[ActionStatus.APPROVAL_PENDING] ? {
          text: actions.statusTotals[ActionStatus.APPROVAL_PENDING].toString(),
          color: `orange`,
        } : undefined,
      },
      {
        name: langauge.generic.action.status.complete,
        onClick: () => setCurrentTab(ActionStatus.COMPLETE),
        icon: CheckCircleIcon,
        href: `#`,
        current: currentTab === ActionStatus.COMPLETE,
        badge: actions?.statusTotals[ActionStatus.COMPLETE] ? {
          text: actions.statusTotals[ActionStatus.COMPLETE].toString(),
          color: `green`,
        } : undefined,
      },
      {
        name: langauge.generic.action.status.reverted,
        onClick: () => setCurrentTab(ActionStatus.REVERTED),
        icon: BackwardIcon,
        href: `#`,
        current: currentTab === ActionStatus.REVERTED,
        badge: actions?.statusTotals[ActionStatus.REVERTED] ? {
          text: actions.statusTotals[ActionStatus.REVERTED].toString(),
          color: ``,
        } : undefined,
      },
      {
        name: langauge.generic.action.status.failed,
        onClick: () => setCurrentTab(ActionStatus.FAILED),
        icon: ExclamationCircleIcon,
        href: `#`,
        current: currentTab === ActionStatus.FAILED,
        badge: actions?.statusTotals[ActionStatus.FAILED] ? {
          text: actions.statusTotals[ActionStatus.FAILED].toString(),
          color: `red`,
        } : undefined,
      },
      {
        name: langauge.generic.action.status.suspended,
        onClick: () => setCurrentTab(ActionStatus.SUSPENDED),
        icon: PauseCircleIcon,
        href: `#`,
        current: currentTab === ActionStatus.SUSPENDED,
        badge: actions?.statusTotals[ActionStatus.SUSPENDED] ? {
          text: actions.statusTotals[ActionStatus.SUSPENDED].toString(),
          color: `orange`,
        } : undefined,
      },
      {
        name: langauge.generic.action.status.skipped,
        onClick: () => setCurrentTab(ActionStatus.SKIPPED),
        icon: NoSymbolIcon,
        href: `#`,
        current: currentTab === ActionStatus.SKIPPED,
        badge: actions?.statusTotals[ActionStatus.SKIPPED] ? {
          text: actions.statusTotals[ActionStatus.SKIPPED].toString(),
          color: `yellow`,
        } : undefined,
      },
      {
        name: `All`,
        onClick: () => setCurrentTab(null),
        icon: MagnifyingGlassCircleIcon,
        href: `#`,
        current: !currentTab,
      },
    ];
  }, [setCurrentTab, currentTab, actions]);

  const tableHeight = useMemo(() => {
    let height = window.innerHeight
      - 180; // px for the header

    if (tabsRef?.current) {
      const tabsHeight = tabsRef.current.clientHeight || 0;
      height = height - tabsHeight;
    }

    return height;
  }, [tabsRef?.current]);

  const showSafeModeWarning = safeModeEnabled && (currentTab === ActionStatus.PENDING || actions?.actions?.some(a => a.status === ActionStatus.PENDING));

  return (
    <div>
      <Tabs
        tabs={ tabs }
        small={ smallTabs }
        className={ `pb-0` }
        ref={ tabsRef }
      />

      <When condition={ showSafeModeWarning }>
        <Strap
          type={ `caution` }
          className={ `rounded-b-md` }
        >
          { `Safe mode is enabled. Scheduled actions will be not run.` }
        </Strap>
      </When>

      <div
        style={ { maxHeight: tableHeight, display: `flex`, flexDirection: `column` } }
      >
        <DatagridCard className={ `h-full` }>
          <DataGridPro
            apiRef={ gridApiRef }
            sx={ {
              ...commonConfig.headerStyle,
              border: `none`,
              [`& .${gridClasses.cell}`]: {
                py: 1,
              },
              '& .MuiDataGrid-toolbar': {
                height: `fit-content`,
                minHeight: `fit-content`,
                paddingTop: `1px`,
                paddingLeft: 0,
              },
            } }
            initialState={ {
              columns: {
                columnVisibilityModel: defaultColumnVisibilityModel,
              },
            } }
            rows={ rows }
            getRowHeight={ () => `auto` }
            columns={ columns }
            loading={ isLoading || isFetching }
            pinnedColumns={ { right: [`actions`] } }
            pageSizeOptions={ [15, 20, 50, 100, 300] }
            disableDensitySelector
            showToolbar
            // Checkboxes
            checkboxSelection
            disableRowSelectionOnClick
            onRowSelectionModelChange={ model => onRowSelectionChangeWrapper(model, rows.map(i => i.id), setRowSelectionModel) }
            rowSelectionModel={ rowSelectionModel }
            // Pagination
            pagination
            paginationMode={ `server` }
            onPaginationModelChange={ setPaginationModel }
            paginationModel={ paginationModel }
            rowCount={ actions?.total || 0 }
            onCellClick={ onCellClick }
            // Filter
            filterDebounceMs={ 300 }
            filterMode={ `server` }
            onFilterModelChange={ setFilterModel }
            filterModel={ filterModel }
            // Sort
            sortingMode={ `server` }
            onSortModelChange={ setSortModel }
            sortModel={ sortModel }
            // Slots
            slots={ {
              toolbar: CustomToolbar,
              filterPanel: ApiSavedFilterPanel,
            } }
            slotProps={ {
              pagination: {
                showFirstButton: true,
                showLastButton: true,
              },
              filterPanel: {
                savedFilterType: SavedFilterType.Action,
                extraGridState: { tab: currentTab },
                exportParams: savedFilterExportParams,
                filterFormProps: {
                  filterColumns: args => filterColumns(args, [
                    `policyNames`,
                    `toEmail`,
                  ]),
                },
                getColumnForNewFilter: getColumnForNewFilter,
                logicOperators: [GridLogicOperator.And],
              },
              columnsManagement: {
                getTogglableColumns,
              },
              toolbar: {
                currentOrg,
                rowSelectionModel,
                onRemoveFilter,
              },
            } }
          />
        </DatagridCard>
      </div>
    </div>
  );
}

function CustomToolbar({ rowSelectionModel, onRemoveFilter }: ToolbarProps) {
  const params = useActionParams();
  const { currentTab } = useActionTableModels();
  const [exportAllActions] = useExportAllActionsMutation();

  async function onExportAll(filename: string | undefined, columns: string[]) {
    // Same params the table query uses, minus paging — the export covers every page
    try {
      const response = await exportAllActions({
        ...params,
        page: undefined,
        limit: undefined,
        filename,
        columns,
      }).unwrap();

      toast.success(`Export queued. The CSV will be emailed to ${response.email} shortly.`);
    }
    catch (e) {
      console.error(e);
      toast.error(`Could not queue the export. Please try again.`);
    }
  }

  return (
    <CustomGenericToolbar
      rowSelectionModel={ rowSelectionModel }
      onExportAll={ onExportAll }
      onRemoveFilter={ onRemoveFilter }
      context={ `actions` }
      leftActions={ <SaveFilterButton type={ SavedFilterType.Action }
        extraGridState={ { tab: currentTab } }
        exportParams={ toSavedFilterExportParams(params) } /> }
    >
      <BulkUpdatesToolbar
        rowSelectionModel={ rowSelectionModel }
      />
    </CustomGenericToolbar>
  );
}
